import { LpSpinner } from '@livepolls/ui-components/src/components/spinner/LpSpinner';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAddLivePollSessionMutation,
  useLivePollsForZoom,
} from 'src/hooks/store-hooks';
import { ILivePollSessionSetting } from 'src/models/livepoll-session-setting.interface';
import { formatPluralizedText } from 'src/utils/getPluralizedText.util';
import styles from './LivePollListScreenForZoom.module.css';
import { WuButton, WuInput, WuModal } from '@npm-questionpro/wick-ui-lib';
import { t } from 'i18next';

export const LivePollListScreenForZoom = () => {
  const [displayPopup, setDisplayPopup] = useState<boolean>(false);
  const [sessionTitle, setSessionTitle] = useState<string>('');
  const [selectedLivePollId, setSelectedLivePollId] = useState<number>(0);
  const [timeLimit, setTimeLimit] = useState<number>(30);
  const addLivePollSessionMutation = useAddLivePollSessionMutation();
  const navigate = useNavigate();
  const {
    isError,
    isLoading,
    error,
    data: livePollsData,
  } = useLivePollsForZoom();

  useEffect(() => {
    const livePollSession = addLivePollSessionMutation.data;
    if (livePollSession) {
      const url = `/zoom/livepolls/${livePollSession.livePollId}/livepoll-sessions/${livePollSession.id}/run`;
      navigate(url);
    }
  }, [addLivePollSessionMutation.data, navigate]);

  const handleAddLivePollSession = async () => {
    const data: ILivePollSessionSetting = {
      name: sessionTitle || 'Zoom Session',
      responseTimeLimitInSecs: timeLimit,
      anonymousResponses: false,
      displayDifficultyLevels: false,
      teamSetting: { enabled: false },
      presenterNotes: false,
    };
    addLivePollSessionMutation.mutate({
      livePollId: selectedLivePollId,
      data: { ...data },
    });
  };

  const openPopup = (livePollId: number, title?: string) => {
    setSelectedLivePollId(livePollId);
    setDisplayPopup(true);
    setSessionTitle(title || '');
  };

  const handleSessionTitle = (event: ChangeEvent<HTMLInputElement>) => {
    setSessionTitle(event.target.value);
  };

  const handleTimeLimit = (event: ChangeEvent<HTMLInputElement>) => {
    const time = Number(event.target.value);
    if (time >= 0 && time <= 300) {
      setTimeLimit(Number(event.target.value));
    }
  };

  if (isError || error) {
    throw error;
  }

  if (isLoading) {
    return <LpSpinner />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.myPollsText}>My Polls</div>
      {livePollsData?.livePolls?.length === 0 && (
        <div className={styles.noPollsText}>No polls available</div>
      )}
      {livePollsData?.livePolls?.map(livePoll => {
        return (
          <div className={styles.card} key={livePoll.id}>
            <div>
              <div className={styles.livePollTitle}>{livePoll.title}</div>
              <div className={styles.cardInfo}>
                <div className={styles.numberOfQuestions}>
                  {formatPluralizedText(
                    livePoll.questionCount || 0,
                    'question',
                  )}
                </div>
                <div>
                  {formatPluralizedText(
                    livePoll.totalParticipants,
                    'participant',
                  )}
                </div>
              </div>
            </div>
            <div className={styles.playCircleRow}>
              <span
                className={`wm-play-circle ${styles.playCircle}`}
                onClick={() => openPopup(livePoll.id, livePoll.title)}
                data-testid="start-livepoll-session"
              />
            </div>
          </div>
        );
      })}
      <WuModal
        open={displayPopup}
        size="sm"
        preventClickOutside={true}
        onOpenChange={() => setDisplayPopup(!displayPopup)}
        maxWidth="300px"
        maxHeight="300px"
      >
        <div className={styles.modalContent}>
          <WuInput
            label="Session title"
            variant="outlined"
            value={sessionTitle}
            onChange={event => handleSessionTitle(event)}
            maxLength={60}
          />

          <div className={styles.characterLimitForSessionName}>
            {sessionTitle.length}/60
          </div>

          <WuInput
            label="Time limit"
            type="number"
            variant="outlined"
            value={timeLimit}
            onChange={event => handleTimeLimit(event)}
            maxLength={60}
          />

          <div className={styles.saveButtonRow}>
            <WuButton
              variant="primary"
              size="sm"
              onClick={handleAddLivePollSession}
            >
              {t('start')}
            </WuButton>
          </div>
        </div>
      </WuModal>
    </div>
  );
};
